import { BillSearchView } from '@enview/interface/types/BillSearch';
import { Person } from '@enview/interface/types/Person';
import { t } from 'i18next';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { trackViewedVoteHistory } from '../../analytics/PersonAnalytics';
import { UserActionAPI } from '../../api';
import { g as abilityGlossary } from '../../config/ability';
import { checkPermission, getBillSearchView } from '../../dux';
import { State, Thunk } from '../../dux/@types';
import { getTeamMode } from '../../dux/TeamModeDux';
import featureFlags, { FrontendFeature } from '../../featureFlags';
import { desktopMediaQuery } from '../../helpers/mediaQueries';
import NavigationTab, { TabComponent } from '../Shared/NavigationTabs/NavigationTab';
import BillsSponsored from './BillsSponsored';
import PersonActivityFeed from './PersonActivityFeed';
import PersonDetails from './PersonDetails';
import PersonStaffDirectory from './PersonStaffDirectory';
import PersonVoteHistory from './PersonVoteHistory';

enum Paths {
  SPONSORED_BILLS = 'bills-sponsored',
  ACTIVITY_FEED = 'activity',
  VOTE_HISTORY = 'vote-history',
  STAFF_DIRECTORY = 'staff-directory',
}

enum Tabs {
  DETAILS = 'Details',
  SPONSORED_BILLS = 'Bills Sponsored',
  ACTIVITY_FEED = 'Activity',
  VOTE_HISTORY = 'Vote History',
  STAFF_DIRECTORY = 'Staff Directory',
}

const DESKTOP_TABS = [
  Tabs.SPONSORED_BILLS,
  Tabs.ACTIVITY_FEED,
  Tabs.VOTE_HISTORY,
  Tabs.STAFF_DIRECTORY,
];

type PersonNavProps = {
  electedOfficialId: number;
  person: Person;
};

const PersonViewNav = (props: PersonNavProps): ReactElement => {
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const teamModeId = useSelector(getTeamMode);
  const { electedOfficialId, person } = props;
  const basePath = `/person/${electedOfficialId}/`;
  const activeTabPath = window.location.pathname.split('/').pop() || '';
  const [isDesktopScreen, setIsDesktopScreen] = useState(desktopMediaQuery.matches);
  const dispatch = useDispatch();
  const hasStaffDirectoryTabEnabled = featureFlags.isFeatureEnabledForUser(
    FrontendFeature.LegislatorStaffDirectoryTab,
    orgUser,
  );
  const canViewStaffDirectoryTab = (): Thunk<boolean> => {
    return dispatch(
      checkPermission(
        abilityGlossary.VIEW,
        abilityGlossary.PERSON_STAFF_DIRECTORY,
        true,
      ),
    );
  };

  // Need to dispatch this action here instead of at root layout
  // due to nefarious Redux bug
  useEffect(() => {
    if (teamModeId && teamModeId !== -1) dispatch(getBillSearchView(teamModeId));
  }, [dispatch, teamModeId]);
  const billSearchView =
    useSelector((state: State) => state.account.billSearchView) ?? BillSearchView.Card;

  const scrollRef = useRef<HTMLDivElement>(null);

  const history = useHistory();
  const params = useMemo(
    () => ({
      teamId: teamModeId,
      includeBillDetails: true,
      personId: person.id,
    }),
    [teamModeId, person.id],
  );
  const { data } = UserActionAPI.endpoints.getUserActions.useQuery(params);

  const handleResize = (): void => {
    setIsDesktopScreen(desktopMediaQuery.matches);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderActivityFeed = (): ReactElement => (
    <PersonActivityFeed orgUser={orgUser} person={person} userActions={data || []} />
  );

  const renderVoteHistory = (): ReactElement => <PersonVoteHistory person={person} />;

  const renderStaffDirectory = (): ReactElement => {
    return hasStaffDirectoryTabEnabled && canViewStaffDirectoryTab() ? (
      <PersonStaffDirectory person={person} />
    ) : (
      <Redirect to={`${basePath}${Paths.SPONSORED_BILLS}`} />
    );
  };

  const getActiveTab = (path: string): string => {
    switch (path) {
      case Paths.SPONSORED_BILLS:
        return Tabs.SPONSORED_BILLS;
      case Paths.ACTIVITY_FEED:
        return Tabs.ACTIVITY_FEED;
      case Paths.VOTE_HISTORY:
        return Tabs.VOTE_HISTORY;
      case Paths.STAFF_DIRECTORY:
        return Tabs.STAFF_DIRECTORY;
      default:
        return isDesktopScreen ? '' : Tabs.DETAILS;
    }
  };

  const getTabPath = (tab: string): string => {
    switch (tab) {
      case Tabs.SPONSORED_BILLS:
        return `${basePath}${Paths.SPONSORED_BILLS}`;
      case Tabs.ACTIVITY_FEED:
        return `${basePath}${Paths.ACTIVITY_FEED}`;
      case Tabs.VOTE_HISTORY:
        return `${basePath}${Paths.VOTE_HISTORY}`;
      case Tabs.STAFF_DIRECTORY:
        return `${basePath}${Paths.STAFF_DIRECTORY}`;
      default:
        return `${basePath}`;
    }
  };

  const getTabs = (): (Tabs | TabComponent)[] => {
    const tabs = isDesktopScreen ? DESKTOP_TABS : Object.values(Tabs);
    // TODO: Specify all legislator tabs in ability profiles and map all tabs here
    const customContent: ReactElement = (
      <Trans
        components={{
          staffLink: <a href={t('urls.legislativeStaff')} target="_blank" />,
          gate: <p className="tooltip-header" />,
        }}
        i18nKey="featureGating.tooltipText.legislatorStaffDirectory"
        key="featureGating.tooltipText.legislatorStaffDirectory"
      />
    );
    return tabs.map((tab) =>
      tab == Tabs.STAFF_DIRECTORY
        ? {
            name: tab,
            component: <>{tab}</>,
            disabled: !hasStaffDirectoryTabEnabled || !canViewStaffDirectoryTab(),
            toolTipMessage: customContent,
          }
        : tab,
    );
  };

  const setCurrentTab = (tab: string): void => {
    if (tab === Tabs.VOTE_HISTORY) trackViewedVoteHistory(person, orgUser);
    history.push(getTabPath(tab));
  };
  return (
    <div ref={scrollRef}>
      <NavigationTab
        currentTab={getActiveTab(activeTabPath)}
        setCurrentTab={setCurrentTab}
        tabs={getTabs()}
      >
        <Switch>
          <Route
            path={`${basePath}${Paths.SPONSORED_BILLS}`}
            render={() => (
              <BillsSponsored
                person={person}
                scrollRef={scrollRef}
                view={billSearchView}
              />
            )}
          />
          <Route
            path={`${basePath}${Paths.ACTIVITY_FEED}`}
            render={renderActivityFeed}
          />
          <Route path={`${basePath}${Paths.VOTE_HISTORY}`} render={renderVoteHistory} />
          <Route
            path={`${basePath}${Paths.STAFF_DIRECTORY}`}
            render={renderStaffDirectory}
          />
          {!isDesktopScreen && (
            <Route path={basePath} render={() => <PersonDetails person={person} />} />
          )}
          {isDesktopScreen && (
            <Redirect from={basePath} to={`${basePath}${Paths.SPONSORED_BILLS}`} />
          )}
        </Switch>
      </NavigationTab>
    </div>
  );
};

export default PersonViewNav;
